/**
 * The meeting video is the City's recording on Vimeo, played with Vimeo's player (its own controls
 * hidden; the design's controls drive it). Same contract as VideoClock.ts for a <video>: push the
 * playhead into the store every animation frame while playing (tick), no React state.
 *
 * Vimeo reports time ~4×/s, so between reports the clock advances by wall time; that keeps the
 * word-by-word highlight and the playhead smooth.
 *
 * Another city's recording is a file on its video site, played in a <video> (attachVideo): the same clock and controls
 * drive it through the same few calls, and it reports time the same way (timeupdate, seeked, play, pause, ended).
 */
import Player from "@vimeo/player";
import { meetingStore } from "../../stores/meetingStore";
import { track } from "../../lib/analytics";

/** What the clock drives: Vimeo's player, or a <video> element behind the same calls. */
interface Driver {
  play(): Promise<unknown>; pause(): Promise<unknown>; setCurrentTime(seconds: number): Promise<unknown>; setMuted(muted: boolean): Promise<unknown>;
  requestFullscreen(): Promise<unknown>; ready(): Promise<unknown>; destroy(): Promise<unknown>;
}
type On = (event: "timeupdate" | "seeked" | "play" | "pause" | "ended", fn: (e: { seconds: number }) => void) => void;

let player: Driver | null = null;
let vimeo: Player | null = null;   // text tracks (setCaptions) are Vimeo's alone
let base = { t: 0, at: 0 }; // last reported time and when it was reported
let playing = false;
let raf = 0;
let pendingSeek: number | null = null; // a deep-link time (?t=) to seek to once the player is ready
let clipEnd: number | null = null;     // a shared clip (?t=a-b) plays only to b (spec 06 card 5)

const now = () => performance.now();
const estimate = () => (playing ? base.t + (now() - base.at) / 1000 : base.t);

function loop() {
  const t = estimate();
  if (clipEnd != null && t >= clipEnd) { const end = clipEnd; clipEnd = null; base = { t: end, at: now() }; pause(); meetingStore.getState().tick(end); return; }
  meetingStore.getState().tick(t);
  raf = playing ? requestAnimationFrame(loop) : 0;
}

// GA4 video_start: the first play of each meeting's video on this page, at the second it started from.
let started: string | null = null;
function videoStarted() {
  const id = meetingStore.getState().meeting?.id;
  if (!id || started === id) return;
  started = id;
  track("video_start", { meeting_id: id, t: Math.floor(estimate()) });
}

// GA4 video_chapter: playback entered an agenda item (played on into it, or jumped to it), once per item per page.
const chaptersSeen = new Set<string>();
export function chapterReached(index: number, start: number) {
  const id = meetingStore.getState().meeting?.id;
  if (!id || !playing || index < 0 || chaptersSeen.has(`${id}:${index}`)) return;
  chaptersSeen.add(`${id}:${index}`);
  track("video_chapter", { meeting_id: id, t: Math.floor(start), chapter: index + 1 });
}

function attach(p: Driver, on: On): () => void {
  player = p;
  const report = ({ seconds }: { seconds: number }) => { base = { t: seconds, at: now() }; };
  on("timeupdate", report);
  on("seeked", (e) => { report(e); meetingStore.getState().tick(e.seconds); });
  on("play", () => { playing = true; base.at = now(); cancelAnimationFrame(raf); raf = requestAnimationFrame(loop); dispatchEvent(new Event("meeting:play")); videoStarted(); });
  on("pause", () => { playing = false; cancelAnimationFrame(raf); meetingStore.getState().tick(base.t); dispatchEvent(new Event("meeting:pause")); });
  on("ended", () => { playing = false; cancelAnimationFrame(raf); dispatchEvent(new Event("meeting:pause")); });
  if (pendingSeek != null) { const t = pendingSeek; pendingSeek = null; void p.ready().then(() => p.setCurrentTime(t)).catch(() => {}); }
  return () => { cancelAnimationFrame(raf); playing = false; p.destroy().catch(() => {}); if (player === p) player = null; };
}

export function attachVimeo(iframe: HTMLIFrameElement): () => void {
  const p = new Player(iframe);
  vimeo = p;
  const detach = attach(p, (event, fn) => p.on(event, fn));
  return () => { detach(); if (vimeo === p) vimeo = null; };
}

/** Another city's recording file in a <video> (VideoStage): driven and timed like Vimeo's player. */
export function attachVideo(video: HTMLVideoElement): () => void {
  const off: (() => void)[] = [];
  const driver: Driver = {
    play: () => video.play(), pause: async () => video.pause(), setCurrentTime: async (s) => { video.currentTime = s; },
    setMuted: async (m) => { video.muted = m; }, requestFullscreen: () => video.requestFullscreen(),
    ready: () => (video.readyState >= 1 ? Promise.resolve() : new Promise((ok) => video.addEventListener("loadedmetadata", ok, { once: true }))),
    destroy: async () => { off.forEach((f) => f()); video.pause(); },
  };
  return attach(driver, (event, fn) => {
    const h = () => fn({ seconds: video.currentTime });
    video.addEventListener(event, h);
    off.push(() => video.removeEventListener(event, h));
  });
}

export const isPlaying = () => playing;
export const currentTime = () => estimate();

export function pause() { void player?.pause().catch(() => {}); }

export function togglePlay() {
  if (!player) return;
  void (playing ? player.pause() : player.play()).catch(() => {});
}

/** Seek helper for timestamp links, speaker faces, the chapter bar and agenda rows (as VideoClock.seekTo). */
export function seekTo(seconds: number) {
  clipEnd = null; // jumping elsewhere leaves the clip
  meetingStore.getState().setFollowMode("following");
  base = { t: seconds, at: now() };
  meetingStore.getState().tick(seconds);
  if (!player) return;
  void player.setCurrentTime(seconds).then(() => player?.play()).catch(() => {});
}

/** Move the playhead without changing play/pause (Skip procedure and its Undo). */
export function jumpTo(seconds: number) {
  clipEnd = null;
  base = { t: seconds, at: now() };
  meetingStore.getState().tick(seconds);
  void player?.setCurrentTime(seconds).catch(() => {});
}

/**
 * Dragging along a seek bar: the playhead (and transcript) follow at once; the player itself is asked to seek at
 * most every 150 ms while dragging, then once more where the drag ends (`final`). Play/pause is left as it was.
 */
let scrubTimer: ReturnType<typeof setTimeout> | null = null;
let scrubTarget = 0;
export function scrubTo(seconds: number, final = false) {
  clipEnd = null;
  scrubTarget = seconds;
  base = { t: seconds, at: now() };
  meetingStore.getState().tick(seconds);
  if (final) {
    if (scrubTimer) { clearTimeout(scrubTimer); scrubTimer = null; }
    meetingStore.getState().setFollowMode("following");
    void player?.setCurrentTime(seconds).catch(() => {});
    return;
  }
  if (scrubTimer) return;
  scrubTimer = setTimeout(() => { scrubTimer = null; void player?.setCurrentTime(scrubTarget).catch(() => {}); }, 150);
}

export function setMuted(muted: boolean) { void player?.setMuted(muted).catch(() => {}); }
export function setCaptions(on: boolean) {
  const v = vimeo;
  if (!v) return;
  void (on ? v.enableTextTrack("en").catch(() => v.getTextTracks().then((t) => t[0] && v.enableTextTrack(t[0].language))) : v.disableTextTrack()).catch(() => {});
}
export function requestFullscreen() { void player?.requestFullscreen().catch(() => {}); }

/** Start at a deep-linked moment: the page shows it at once; the player seeks when it's ready.
 *  With an end (a shared clip), playback stops there. */
export function startAt(seconds: number, end: number | null = null) {
  clipEnd = end != null && end > seconds ? end : null;
  base = { t: seconds, at: now() };
  meetingStore.getState().tick(seconds);
  if (player) void player.setCurrentTime(seconds).catch(() => {});
  else pendingSeek = seconds;
}
